import React, { useState, useEffect, useCallback } from 'react';
import { IconSearch, IconGrid, IconList, IconChevronUp } from './Icons';
import { supabase } from '../lib/supabase';
import './Logs.css';

const PER_PAGE_OPTIONS = [10, 25, 50, 100, 250, 500];
const PER_PAGE_ALL = 10000; // "All" – fetch up to this many (Supabase may cap at 1000)

function formatLogDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatDetails(details) {
  if (details == null) return '—';
  if (typeof details === 'string') return details;
  try {
    const s = JSON.stringify(details);
    return s.length > 200 ? s.slice(0, 200) + '…' : s;
  } catch {
    return '—';
  }
}

function displayMember(row) {
  const name = row.profiles?.full_name || row.profiles?.username;
  if (name) return name.trim() || '—';
  return row.user_id ? 'Unknown' : '—';
}

export default function Logs() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [showAll, setShowAll] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [sortMember, setSortMember] = useState('desc');
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const effectivePerPage = showAll ? PER_PAGE_ALL : perPage;
  const totalPages = Math.max(1, Math.ceil(totalCount / effectivePerPage));
  const from = totalCount === 0 ? 0 : (page - 1) * effectivePerPage + 1;
  const to = Math.min(page * effectivePerPage, totalCount);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    const fromRow = (page - 1) * effectivePerPage;
    const toRow = showAll ? PER_PAGE_ALL - 1 : fromRow + perPage - 1;
    let query = supabase
      .from('activity_logs')
      .select('id, created_at, user_id, action, entity_type, entity_id, title, details, profiles(full_name)', { count: 'exact' })
      .order('created_at', { ascending: sortMember === 'asc' });

    if (showAll) {
      query = query.limit(PER_PAGE_ALL);
    } else {
      query = query.range(fromRow, toRow);
    }

    const term = (search || '').trim().toLowerCase();
    if (term) {
      query = query.or(`title.ilike.%${term}%,entity_type.ilike.%${term}%,action.ilike.%${term}%`);
    }

    const { data, error: err, count } = await query;
    if (err) {
      const msg = err.message || '';
      const isMissingTable = /schema cache|relation .* does not exist|activity_logs/i.test(msg);
      setError(isMissingTable
        ? 'Activity logs table is not set up. In Supabase Dashboard go to SQL Editor, then run the migration: supabase/migrations/036_activity_logs.sql'
        : msg);
      setLogs([]);
      setTotalCount(0);
    } else {
      setLogs(data ?? []);
      setTotalCount(count ?? 0);
    }
    setLoading(false);
  }, [page, perPage, showAll, effectivePerPage, search, sortMember]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleSortMember = () => {
    setSortMember((s) => (s === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  return (
    <div className="logs-page">
      <div className="logs-header">
        <h1 className="logs-title">Activity Logs</h1>
      </div>

      <div className="logs-toolbar">
        <div className="logs-view-mode">
          <button
            type="button"
            className={`logs-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <IconGrid />
          </button>
          <button
            type="button"
            className={`logs-view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <IconList />
          </button>
        </div>
        <button type="button" className="logs-toolbar-btn">Excel</button>
        <form className="logs-search-wrap" onSubmit={handleSearchSubmit}>
          <IconSearch />
          <input
            type="search"
            className="logs-search"
            placeholder="Search by title, type, or action"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search logs"
          />
        </form>
      </div>

      {error && (
        <div className="logs-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className="logs-loading">Loading…</p>
      ) : (
        <>
          <div className="logs-table-wrap">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="logs-th-sortable">
                    <button
                      type="button"
                      className="logs-th-sort"
                      onClick={handleSortMember}
                    >
                      Team member
                      <IconChevronUp className={`logs-sort-icon ${sortMember === 'desc' ? 'desc' : ''}`} />
                    </button>
                  </th>
                  <th>Action</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Record ID</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="logs-empty">
                      No activity logs yet.
                    </td>
                  </tr>
                ) : (
                  logs.map((row) => (
                    <tr key={row.id}>
                      <td>{formatLogDate(row.created_at)}</td>
                      <td>{displayMember(row)}</td>
                      <td>{row.action || '—'}</td>
                      <td>{row.entity_type || '—'}</td>
                      <td>{row.title || '—'}</td>
                      <td>{row.entity_id ?? '—'}</td>
                      <td className="logs-td-details" title={formatDetails(row.details)}>{formatDetails(row.details)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="logs-pagination">
            <div className="logs-pagination-left">
              <select
                className="logs-per-page"
                value={showAll ? 'all' : perPage}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'all') {
                    setShowAll(true);
                    setPerPage(50);
                    setPage(1);
                  } else {
                    setShowAll(false);
                    setPerPage(Number(v) || 50);
                    setPage(1);
                  }
                }}
                aria-label="Per page"
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
                <option value="all">All</option>
              </select>
              <span className="logs-pagination-range">
                {totalCount === 0 ? '0' : `${from}-${to}`} / {totalCount}
              </span>
            </div>
            <div className="logs-pagination-right">
              <button type="button" className="logs-page-btn" disabled={page <= 1} onClick={() => setPage(1)} title="First page">«</button>
              <button type="button" className="logs-page-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} title="Previous">‹</button>
              <span className="logs-page-num">{page}</span>
              <button type="button" className="logs-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} title="Next">›</button>
              <button type="button" className="logs-page-btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Last page">»</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
